import type { LayoutItem } from '@vision360/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Local layout state for one dashboard (PRD VS-041, TechSpec §8.1 / §9.5):
 * every change applies at once, is saved 500 ms after the last one, and can be
 * undone (20 levels). Switching dashboards or leaving flushes a pending save.
 */

export const AUTOSAVE_MS = 500;
export const UNDO_DEPTH = 20;

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function useLayoutEditor(
  dashboardId: string | undefined,
  serverLayout: LayoutItem[] | undefined,
  save: (id: string, layout: LayoutItem[]) => Promise<unknown>,
) {
  const [items, setItems] = useState<LayoutItem[]>(serverLayout ?? []);
  const [canUndo, setCanUndo] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  // The source of truth for commit/undo, so they never run side effects
  // inside a state updater; the state above only drives rendering.
  const itemsRef = useRef<LayoutItem[]>(items);
  const undoRef = useRef<LayoutItem[][]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pending = useRef<{ id: string; layout: LayoutItem[] } | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const show = useCallback((next: LayoutItem[], stack: LayoutItem[][]) => {
    itemsRef.current = next;
    undoRef.current = stack;
    setItems(next);
    setCanUndo(stack.length > 0);
  }, []);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = undefined;
    const job = pending.current;
    pending.current = null;
    if (!job) return;
    setSaveState('saving');
    saveRef.current(job.id, job.layout).then(
      () => setSaveState(pending.current ? 'saving' : 'saved'),
      () => setSaveState('error'),
    );
  }, []);

  // A different dashboard: save what is pending for the old one, start clean.
  const loaded = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (dashboardId === loaded.current || serverLayout === undefined) return;
    flush();
    loaded.current = dashboardId;
    show(serverLayout, []);
    setSaveState('idle');
  }, [dashboardId, serverLayout, flush, show]);

  // Leaving the page never loses the last change.
  useEffect(() => flush, [flush]);

  const schedule = useCallback(
    (layout: LayoutItem[]) => {
      if (!dashboardId) return;
      pending.current = { id: dashboardId, layout };
      clearTimeout(timer.current);
      timer.current = setTimeout(flush, AUTOSAVE_MS);
    },
    [dashboardId, flush],
  );

  /** A user change: undoable unless told otherwise, saved soon. */
  const commit = useCallback(
    (next: LayoutItem[], options: { undoable?: boolean } = {}) => {
      const current = itemsRef.current;
      if (next === current) return;
      const stack =
        options.undoable === false
          ? undoRef.current
          : [...undoRef.current.slice(-(UNDO_DEPTH - 1)), current];
      show(next, stack);
      schedule(next);
    },
    [schedule, show],
  );

  const undo = useCallback(() => {
    const previous = undoRef.current.at(-1);
    if (!previous) return;
    show(previous, undoRef.current.slice(0, -1));
    schedule(previous);
  }, [schedule, show]);

  /** The live layout, for event handlers that must not wait for a render. */
  const current = useCallback(() => itemsRef.current, []);

  return { items, current, commit, undo, canUndo, saveState };
}
